import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { quotesApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtMoney, fmtDate, fmtDateTime, statusTone, prettyStatus, apiError } from "@/lib/format";
import { groupQuoteItems, lineTotal } from "@/lib/quoteGroups";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { SignaturePad } from "@/components/SignaturePad";

export default function PortalQuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: quote, loading, error, reload } = useAsync(() => quotesApi.getOne(id), [id]);
  const [approveOpen, setApproveOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  if (loading) return <div className="mx-auto max-w-3xl"><Back /><LoadingState label="Loading quote…" /></div>;
  if (error) return <div className="mx-auto max-w-3xl"><Back /><ErrorState message={error} onRetry={reload} /></div>;
  if (!quote) return null;

  const grouped = groupQuoteItems(quote.items || []);
  const groups = [
    ...grouped.doorGroups.map((g) => ({ key: g.key, label: `${g.door_reference || "Door"}${g.door_location ? ` · ${g.door_location}` : ""}`, items: g.items })),
    ...(grouped.additional.length ? [{ key: "__additional__", label: "Additional works", items: grouped.additional }] : []),
  ];
  const canDecide = quote.status === "sent";

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Back />
      <PageHeader
        title={quote.title || `Quote ${String(id).slice(-6)}`}
        subtitle={quote.building_name || ""}
        actions={<Badge variant="outline" className={statusTone(quote.status)}>{prettyStatus(quote.status)}</Badge>}
      />

      {quote.status === "accepted" && (
        <Card className="flex items-center gap-3 border-emerald-100 bg-emerald-50 p-4" data-testid="portal-quote-accepted">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
          <div className="text-sm">
            <p className="font-semibold text-emerald-800">
              Accepted{quote.decision_signature_name ? ` by ${quote.decision_signature_name}` : ""}
            </p>
            {quote.decision_at && <p className="text-emerald-700">{fmtDateTime(quote.decision_at)}</p>}
          </div>
        </Card>
      )}
      {quote.status === "declined" && (
        <Card className="border-red-100 bg-red-50 p-4 text-sm font-semibold text-red-700" data-testid="portal-quote-declined">Quote declined{quote.decision_note ? ` — ${quote.decision_note}` : ""}</Card>
      )}

      {groups.map((g) => (
        <Card key={g.key} className="overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50 px-4 py-2.5">
            <p className="text-sm font-semibold text-slate-700">{g.label}</p>
          </div>
          <div className="divide-y divide-slate-50">
            {g.items.map((it, i) => (
              <div key={i} className="flex items-start justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">{it.component || it.description || "Item"}</p>
                  {it.description && it.component && <p className="text-sm text-slate-500">{it.description}</p>}
                  {Number(it.quantity || 1) > 1 && <p className="text-xs text-slate-400">Qty {it.quantity} × {fmtMoney(it.cost)}</p>}
                </div>
                <span className="shrink-0 font-semibold text-slate-800">{fmtMoney(lineTotal(it))}</span>
              </div>
            ))}
          </div>
        </Card>
      ))}

      <Card className="p-4">
        <Row label="Subtotal" value={fmtMoney(quote.subtotal ?? quote.total)} />
        {Number(quote.discount_amount || 0) > 0 && <Row label={`Discount${quote.discount_label ? ` (${quote.discount_label})` : ""}`} value={`- ${fmtMoney(quote.discount_amount)}`} />}
        {Number(quote.vat_amount || 0) > 0 && <Row label="VAT" value={fmtMoney(quote.vat_amount)} />}
        <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
          <span className="font-semibold text-slate-900">Total</span>
          <span className="text-lg font-bold text-slate-900" data-testid="portal-quote-total">{fmtMoney(quote.total)}</span>
        </div>
      </Card>

      {quote.notes && <Card className="p-4 text-sm text-slate-600"><p className="mb-1 font-semibold text-slate-700">Notes</p>{quote.notes}</Card>}

      {canDecide && (
        <div className="sticky bottom-0 flex gap-3 border-t border-slate-200 bg-white/95 py-4 backdrop-blur">
          <Button variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDeclineOpen(true)} data-testid="portal-decline-btn">
            <XCircle className="mr-2 h-4 w-4" /> Decline
          </Button>
          <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => setApproveOpen(true)} data-testid="portal-approve-btn">
            <CheckCircle2 className="mr-2 h-4 w-4" /> Approve quote
          </Button>
        </div>
      )}

      <ApproveDialog open={approveOpen} onOpenChange={setApproveOpen} quoteId={id} onDone={reload} />
      <DeclineDialog open={declineOpen} onOpenChange={setDeclineOpen} quoteId={id} onDone={reload} />
    </div>
  );
}

function ApproveDialog({ open, onOpenChange, quoteId, onDone }) {
  const [name, setName] = useState("");
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) { toast.error("Please type your name to sign"); return; }
    setBusy(true);
    try {
      await quotesApi.decision(quoteId, { decision: "accepted", signature_name: name.trim(), signature_data_uri: signature || undefined });
      toast.success("Thank you — quote accepted");
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(apiError(e, "Could not submit")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Approve quote</DialogTitle>
          <DialogDescription>Sign to confirm you accept this quote and authorise the work.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Your full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Smith" data-testid="portal-sign-name" />
          </div>
          <div>
            <Label>Signature</Label>
            <SignaturePad value={signature} onChange={setSignature} testId="portal-signature-pad" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={submit} data-testid="portal-approve-confirm">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Confirm approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeclineDialog({ open, onOpenChange, quoteId, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await quotesApi.decision(quoteId, { decision: "declined", note: note.trim() || undefined });
      toast.success("Quote declined");
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(apiError(e, "Could not submit")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Decline quote</DialogTitle>
          <DialogDescription>Let the contractor know why (optional).</DialogDescription>
        </DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional)" rows={3} data-testid="portal-decline-note" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" disabled={busy} onClick={submit} data-testid="portal-decline-confirm">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Decline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value}</span>
    </div>
  );
}

function Back() {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate("/portal/quotes")} className="mb-3 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
      <ArrowLeft className="h-4 w-4" /> Back to quotes
    </button>
  );
}
